import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthStore } from '../../stores/auth';
import type { ColumnConfig } from '../../hooks/useColumnConfig';
import {
  STORAGE_PREFIX,
  MIN_COLUMN_WIDTH,
  MAX_COLUMN_WIDTH,
  clampWidth,
  mergeColumns,
  parseColumnState,
  readStorage,
  toStoredColumns,
  updateColumnState,
  type ColumnState,
} from './columnState';

/** MDF-209: the active Data Library tab, so each tab saves its own layout. */
export const DataLibraryTabContext = createContext<string | null>(null);

/** The storage key for the active tab and user, or null when either is unknown. */
export function useTableLayoutKey(): string | null {
  const tab = useContext(DataLibraryTabContext);
  const userId = useAuthStore(s => s.user?.id);
  return tab && userId ? `${STORAGE_PREFIX}${userId}.table.${tab}` : null;
}

/**
 * One part of the saved tab layout. The value also lives in memory, so a
 * change lasts for the visit when storage is blocked or the key is null. Two
 * tabs can share one component instance, so the value reloads when the key
 * changes.
 */
function useStoredPart<P extends keyof ColumnState>(key: string | null, part: P) {
  const load = useCallback((k: string | null) => (k ? parseColumnState(readStorage(k))[part] : undefined), [part]);
  const [state, setState] = useState(() => ({ key, value: load(key) }));
  // React renders again at once with the reloaded value.
  if (state.key !== key) setState({ key, value: load(key) });

  // MDF-38: the toolbar and the table each read the layout, so a change in one
  // (a drag, or a saved view applied) reaches the other. The event carries the
  // value, so the change also lasts when storage is full or blocked.
  const self = useRef({});
  useEffect(() => {
    if (!key) return;
    const receive = (e: Event) => {
      const change = (e as CustomEvent<LayoutChange>).detail;
      if (change.key === key && change.part === part && change.source !== self.current) {
        setState({ key, value: change.value as ColumnState[P] });
      }
    };
    window.addEventListener(LAYOUT_CHANGE_EVENT, receive);
    return () => window.removeEventListener(LAYOUT_CHANGE_EVENT, receive);
  }, [key, part]);

  const update = useCallback(
    (change: (previous: ColumnState[P]) => ColumnState[P]) => {
      const value = change(state.value);
      setState({ key: state.key, value });
      if (state.key) {
        updateColumnState(state.key, s => ({ ...s, [part]: value }));
        const detail: LayoutChange = { key: state.key, part, value, source: self.current };
        window.dispatchEvent(new CustomEvent(LAYOUT_CHANGE_EVENT, { detail }));
      }
    },
    [state, part],
  );
  return [state.value, update] as const;
}

const LAYOUT_CHANGE_EVENT = 'medley:data-library-layout';

interface LayoutChange {
  key: string;
  part: keyof ColumnState;
  value: unknown;
  /** The hook instance that sent the change, so it does not apply it twice. */
  source: object;
}

/**
 * MDF-209: column order and visibility for the active tab, saved per user.
 * Pass persist false for a preset view (a health drill-down) so it neither
 * reads nor overwrites the saved layout.
 */
export function useDataLibraryColumns(defaults: ColumnConfig[], persist = true) {
  const key = useTableLayoutKey();
  const [stored, update] = useStoredPart(persist ? key : null, 'columns');
  const columns = useMemo(() => mergeColumns(defaults, stored), [defaults, stored]);
  const setColumns = useCallback(
    (next: ColumnConfig[]) => update(previous => toStoredColumns(next, previous)),
    [update],
  );
  return { columns, setColumns };
}

export interface ColumnWidthsLayout {
  columnWidths: Record<string, number>;
  onColumnResize: (columnId: string, width: number) => void;
  onColumnResetWidth: (columnId: string) => void;
  setColumnWidths: (widths: Record<string, number>) => void;
}

/** MED-1308: column widths set by drag for the active tab, saved per user. */
export function useDataLibraryColumnWidths(): ColumnWidthsLayout {
  const [stored, update] = useStoredPart(useTableLayoutKey(), 'widths');
  const onColumnResize = useCallback(
    (columnId: string, width: number) =>
      update(previous => ({
        ...previous,
        [columnId]: clampWidth(width, MIN_COLUMN_WIDTH, MAX_COLUMN_WIDTH),
      })),
    [update],
  );
  const onColumnResetWidth = useCallback(
    (columnId: string) =>
      update(previous => {
        const next = { ...previous };
        delete next[columnId];
        return next;
      }),
    [update],
  );
  // MDF-38: a saved view replaces every width at once.
  const setColumnWidths = useCallback((widths: Record<string, number>) => update(() => widths), [update]);
  return { columnWidths: stored ?? {}, onColumnResize, onColumnResetWidth, setColumnWidths };
}
